import ProductDrawerAction from "@/components/drawers/ProductDrawer/ProductDrawerAction";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTableState from "@/components/Table/hooks/useTableState";
import Table, { Column } from "@/components/Table/Table";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import useImpactMetric from "@/pages/dashboard/hooks/useImpactMetric";
import {
  ProductBy,
  SingleProductImpactDetailPerLifeCycle,
  useProductImpactDetail,
} from "@/pages/dashboard/hooks/useProductImpact";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { IMPACT_METRICS } from "@/pages/dashboard/utils/types";
import { useEffect } from "react";
import { FacilityImpactDetailMode } from "./ProductImpactSideBySide";

export default function ProductImpactDetailsPerScope({
  mode,
  by,
}: {
  mode: FacilityImpactDetailMode;
  by: ProductBy;
}) {
  const metric = useImpactMetric();
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({ defaultPagination: { page: 1, pageSize: 10 } });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const columns: Column<SingleProductImpactDetailPerLifeCycle>[] = [
    {
      label: "Product",
      key: "product_name",
      sortable: true,
      searchable: true,
      maxWidth: "2fr",
      renderer: (record) => (
        <CrossReference to={`/products/${record.id}`}>
          <ProductDrawerAction
            productId={record.id}
            metric={IMPACT_METRICS["carbon_footprint"]}
            by={by}
          >
            {record.product_name}
          </ProductDrawerAction>
        </CrossReference>
      ),
    },
    {
      label: "Scope 1",
      key: "carbon_footprint_scope_1",
      sortable: true,
      renderer: (record) =>
        ImpactFormatter(
          record?.impact.product.carbon_footprint_scope_1,
          data?.total_impact?.product.carbon_footprint_scope_1
        ),
    },
    {
      label: "Scope 2",
      key: "carbon_footprint_scope_2",
      sortable: true,
      renderer: (record) =>
        ImpactFormatter(
          record?.impact.product.carbon_footprint_scope_2,
          data?.total_impact?.product.carbon_footprint_scope_2
        ),
    },
    {
      label: "Scope 3",
      key: "carbon_footprint_scope_3",
      sortable: true,
      renderer: (record) =>
        ImpactFormatter(
          record?.impact.product.carbon_footprint_scope_3,
          data?.total_impact?.product.carbon_footprint_scope_3
        ),
    },
    {
      label: "Carbon footprint",
      sortable: true,
      key: "carbon_footprint",
      renderer: (record) =>
        ImpactFormatter(
          record?.impact.product.carbon_footprint,
          data?.total_impact?.product.carbon_footprint
        ),
    },
  ];

  const { data, isLoading, isFetching } = useProductImpactDetail({
    by,
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  const ImpactFormatter = (value: number, total?: number | null) => {
    if (mode === "Absolute") {
      return IMPACT_METRICS.carbon_footprint.formatter(value);
    } else {
      return total ? formatPercentage(value / total) : NaN;
    }
  };

  return (
    <Table
      filter={filter}
      hideRaster={true}
      type={"grey"}
      sort={sort}
      search={search}
      columns={columns}
      data={data?.data}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      totalRows={data?.total_rows}
      rowKey={"key"}
    />
  );
}
